// S03/S04/S06/S07/S08/S09/S10/S11/S13/S14 — sales assessments, kits and planners.
import { useMemo, useState } from "react";
import { pipelineHealth, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, ToolSection, Note, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button type="button" data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4">
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- S03 Pipeline Hygiene Grader ---------- */
const S03_DEFAULT = [
  { name: "Acme renewal", value: 600000, probPct: 60, lastActivityDays: 4, evidence: 2 },
  { name: "Nova Industries", value: 1200000, probPct: 50, lastActivityDays: 38, evidence: 0 },
  { name: "Kaveri Group", value: 400000, probPct: 30, lastActivityDays: 12, evidence: 1 },
  { name: "Delta Corp", value: 900000, probPct: 70, lastActivityDays: 55, evidence: 0 },
];
export function ToolS03() {
  const [deals, setDeals] = useState(S03_DEFAULT);
  const r = useMemo(() => pipelineHealth(deals), [deals]);
  const setD = (i, k) => (val) => setDeals((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-S03">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="s03-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
              {["Deal", "Value ₹", "Stage prob %", "Days since activity", "Evidence (0=none,1=claimed,2=buyer-verified)"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {deals.map((d, i) => (
              <tr key={i} className="border-b border-black/10" data-testid={`s03-deal-${i}`}>
                <td className="py-2 pr-3"><input value={d.name} onChange={(e) => setD(i, "name")(e.target.value)} className="w-32 bg-transparent outline-none border-b border-transparent focus:border-black text-sm" /></td>
                {["value", "probPct", "lastActivityDays", "evidence"].map((k) => (
                  <td key={k} className="py-2 pr-3"><input type="number" value={d[k]} onChange={(e) => setD(i, k)(e.target.value)} className="w-20 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`s03-${k}-${i}`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setDeals((s) => [...s, { name: `Deal ${s.length + 1}`, value: 0, probPct: 30, lastActivityDays: 0, evidence: 0 }])} className="mt-4 text-sm font-medium underline underline-offset-4" data-testid="s03-add">+ Add deal</button>
      <div className="mt-8 grid md:grid-cols-2 gap-8">
        <div data-testid="s03-result">
          <ResultRow label="Pipeline value" value={inr(r.totalValue)} />
          <ResultRow label="Weighted pipeline" value={inr(r.weightedValue)} />
          <ResultRow label="Trustworthy pipeline (evidenced + active)" value={inr(r.trustworthyValue)} strong tone={r.trustworthyValue < r.weightedValue * 0.6 ? "bad" : "good"} />
          <ResultRow label="Hygiene score" value={`${r.hygieneScore}/100 — ${r.grade}`} strong tone={r.grade === "wishlist" ? "bad" : "good"} />
          <ResultRow label="Stale deals (>21 days silent)" value={`${r.staleCount} · ${inr(r.staleValue)}`} tone={r.staleCount ? "bad" : "good"} />
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Cleanup agenda</div>
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-700" data-testid="s03-cleanup">
            {r.cleanup.map((c, i) => <li key={i}>{c}</li>)}
            {r.cleanup.length === 0 && <li>Clean pipeline. Protect the standard: no deal advances without buyer-verified evidence.</li>}
          </ul>
        </div>
      </div>
      <Note>A forecast is only as good as its weakest deal. Evidence means the buyer confirmed it — a next step dated by them, a budget mentioned by them — not the rep's optimism.</Note>
    </div>
  );
}

/* ---------- S04 Sales Process Blueprint ---------- */
const S04_DEFAULT = [
  { stage: "Lead captured", entry: "Enquiry received from any source", exit: "Contact established within 24h", owner: "SDR" },
  { stage: "Qualified", entry: "Budget, authority, need, timeline answered", exit: "Discovery call done, pain documented", owner: "Sales rep" },
  { stage: "Proposal", entry: "Solution agreed in principle on a call", exit: "Proposal sent with dated follow-up", owner: "Sales rep" },
  { stage: "Negotiation", entry: "Buyer responds with terms", exit: "Verbal yes with start date", owner: "Sales head" },
  { stage: "Closed", entry: "PO / advance received", exit: "Handover note to delivery", owner: "Sales head" },
];
export function ToolS04() {
  const [stages, setStages] = useState(S04_DEFAULT);
  const setS = (i, k) => (e) => setStages((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target.value } : x)));
  const gaps = stages.filter((s) => !s.entry.trim() || !s.exit.trim() || !s.owner.trim()).length;
  return (
    <div data-testid="tool-S04">
      <p className="text-sm text-zinc-600 mb-5">A stage is a rule, not a label. Define entry criteria, exit criteria and an owner for each — that's what a CRM can actually enforce.</p>
      <div className="space-y-3">
        {stages.map((s, i) => (
          <div key={i} className="border border-zinc-200 p-4 grid md:grid-cols-12 gap-3" data-testid={`s04-stage-${i}`}>
            <div className="md:col-span-3">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Stage {i + 1}</span>
              <input value={s.stage} onChange={setS(i, "stage")} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black" data-testid={`s04-name-${i}`} />
            </div>
            {[["entry", "Entry rule"], ["exit", "Exit rule"], ["owner", "Owner"]].map(([k, label]) => (
              <label key={k} className="md:col-span-3 block">
                <span className="text-xs text-zinc-500">{label}</span>
                <input value={s[k]} onChange={setS(i, k)} className="mt-1 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`s04-${k}-${i}`} />
              </label>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-6">
        <button type="button" onClick={() => setStages((s) => [...s, { stage: "", entry: "", exit: "", owner: "" }])} className="text-sm font-medium underline underline-offset-4" data-testid="s04-add">+ Add stage</button>
        <CopyButton testid="s04-copy" getText={() => `SALES PROCESS BLUEPRINT\n\n${stages.map((s, i) => `${i + 1}. ${s.stage}\n   Entry: ${s.entry}\n   Exit: ${s.exit}\n   Owner: ${s.owner}`).join("\n")}`} />
      </div>
      <div className="mt-4 max-w-md" data-testid="s04-result">
        <ResultRow label="Stages defined" value={String(stages.length)} />
        <ResultRow label="Stages with missing rules" value={String(gaps)} tone={gaps ? "bad" : "good"} strong />
      </div>
      <Note tone={gaps ? "warn" : "info"}>{gaps ? "Every stage needs all three fields — a stage without an exit rule is where deals go to age silently." : "Enforceable blueprint. Configure your CRM so deals cannot move stages without the exit rule's evidence."}</Note>
    </div>
  );
}

/* ---------- S06 Key Account Growth Planner ---------- */
export function ToolS06() {
  const [plan, setPlan] = useState({ account: "Acme Corp", currentRevenue: 5000000, potentialRevenue: 9000000, whitespace: "Their Pune and Indore plants still buy the category from a regional supplier", risks: "Champion retires in Q3; contract renewal in 5 months", nextSteps: "Map plant heads in Pune/Indore by 15th; reference visit from existing site", qbrDate: "" });
  const coverage = plan.potentialRevenue > 0 ? Math.min(100, (plan.currentRevenue / plan.potentialRevenue) * 100) : 0;
  return (
    <div data-testid="tool-S06" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Account name" type="text" value={plan.account} onChange={(v) => setPlan((s) => ({ ...s, account: v }))} testid="s06-account" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Current annual revenue (₹)" value={plan.currentRevenue} onChange={(v) => setPlan((s) => ({ ...s, currentRevenue: v }))} testid="s06-current" />
          <Field label="Realistic potential (₹)" value={plan.potentialRevenue} onChange={(v) => setPlan((s) => ({ ...s, potentialRevenue: v }))} testid="s06-potential" />
        </div>
        {[["whitespace", "White space — what they buy elsewhere that you could serve"], ["risks", "Risks — relationships, renewals, competitors circling"], ["nextSteps", "Next 3 moves with dates and owners"]].map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <textarea value={plan[k]} onChange={(e) => setPlan((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`s06-${k}`} />
          </label>
        ))}
        <Field label="Next QBR date" type="text" value={plan.qbrDate} onChange={(v) => setPlan((s) => ({ ...s, qbrDate: v }))} testid="s06-qbr" />
      </div>
      <div>
        <div className="border-2 border-black p-6 bg-white" data-testid="s06-preview">
          <div className="text-xs uppercase tracking-widest text-zinc-500">One-page account plan</div>
          <h3 className="font-serif-display text-2xl mt-2">{plan.account || "Account"}</h3>
          <div className="mt-4">
            <ResultRow label="Current revenue" value={inr(Number(plan.currentRevenue) || 0)} />
            <ResultRow label="Potential" value={inr(Number(plan.potentialRevenue) || 0)} />
            <ResultRow label="Share of account" value={`${Math.round(coverage)}%`} strong tone={coverage < 50 ? "bad" : "good"} />
          </div>
          {[["White space", plan.whitespace], ["Risks", plan.risks], ["Next moves", plan.nextSteps], ["Next QBR", plan.qbrDate]].map(([l, val]) => (
            <div key={l} className="mt-3">
              <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">{l}</div>
              <p className="mt-1 text-sm text-zinc-800 leading-relaxed">{val || <span className="italic text-zinc-300">Not planned</span>}</p>
            </div>
          ))}
        </div>
        <div className="mt-4"><CopyButton testid="s06-copy" getText={() => `ACCOUNT PLAN — ${plan.account}\nShare of account: ${Math.round(coverage)}%\n\nWHITE SPACE\n${plan.whitespace}\n\nRISKS\n${plan.risks}\n\nNEXT MOVES\n${plan.nextSteps}\n\nQBR: ${plan.qbrDate}`} /></div>
        <Note>An account you serve at 40% share is a prospect you already have access to. Growing it is almost always cheaper than winning a new logo.</Note>
      </div>
    </div>
  );
}

/* ---------- S07 Negotiation Simulator (branching) ---------- */
const S07_TREE = {
  start: {
    text: "Your largest distributor (18% of sales) demands 5 extra margin points, citing a competitor's offer. Your current margin to them is 22%; your contribution on their volume is ₹38 L/year. What do you open with?",
    choices: [
      { label: "Concede 3 points immediately to 'save the relationship'", to: "concede", cost: "−₹23 L/yr contribution", note: "Instant concessions teach partners that pressure works. Expect a fresh demand next quarter — with the competitor's 'offer' again." },
      { label: "Refuse flatly and restate your policy", to: "refuse", cost: "₹0 now, relationship risk", note: "Principled but blind. You never learned whether the competitor offer is real, matched, or a bluff — and a rigid 'no' invites escalation." },
      { label: "Ask what the competitor offer actually includes", to: "probe1", cost: "₹0", note: "Good move. Information before money." },
    ],
  },
  probe1: {
    text: "They show a term sheet: 2 extra points, but with a 45-day credit clause and exclusive display requirements. It's weaker than claimed. Your response?",
    choices: [
      { label: "Offer 1 point tied to verified secondary growth of 15%", to: "conditional", cost: "−₹7.7 L only if growth is delivered", note: "Margin for performance is trade marketing, not concession. The condition protects you if growth doesn't come." },
      { label: "Match their claimed 5 points to 'stay competitive'", to: "match", cost: "−₹38 L/yr contribution", note: "You matched a number they inflated. That's the most expensive sentence in channel management." },
      { label: "Offer non-price value instead: faster claims + a co-funded display programme", to: "nonprice", cost: "−₹4 L/yr programme cost", note: "Often the winning move — partners want earnings and ease, and claims speed is margin in disguise." },
    ],
  },
  conditional: { text: "Outcome: the distributor accepts margin-for-growth. Two quarters on, secondary has risen 12% — short of the 15% condition, so the point is withheld as agreed. Debrief: conditions convert concessions into investments — and they only work if you enforce them when the number falls short.", terminal: true },
  nonprice: { text: "Outcome: claims SLA (15 days) plus a display programme closes the negotiation at a fraction of the ask. Debrief: most 'margin' demands are really 'ease and earnings' demands.", terminal: true },
  concede: { text: "Outcome: 3 points conceded. Two quarters later they're back with a new 'competitor offer'. Debrief: unconditioned concessions are never final — they are opening positions.", terminal: true },
  refuse: { text: "Outcome: they hold volume steady but quietly give the rival a second shelf facing. Debrief: you won the point and lost a little ground — a probe first would have cost nothing.", terminal: true },
  match: { text: "Outcome: margin resets 5 points higher across the network as word spreads. Debrief: matching unverified claims sets your price floor by negotiation — forever.", terminal: true },
};
export function ToolS07() {
  const [node, setNode] = useState("start");
  const [path, setPath] = useState([]);
  const n = S07_TREE[node];
  const choose = (c) => { setPath((p) => [...p, { choice: c.label, cost: c.cost, note: c.note }]); setNode(c.to); };
  const reset = () => { setNode("start"); setPath([]); };
  return (
    <div data-testid="tool-S07">
      <div className="border-2 border-black p-6 bg-white" data-testid="s07-node">
        <div className="text-xs uppercase tracking-widest text-zinc-500">{n.terminal ? "Debrief" : `Move ${path.length + 1}`}</div>
        <p className="mt-3 font-serif-display text-xl leading-snug">{n.text}</p>
        {!n.terminal && (
          <div className="mt-5 space-y-2">
            {n.choices.map((c, i) => (
              <button key={i} type="button" onClick={() => choose(c)} className="block w-full text-left border border-zinc-300 p-3.5 text-sm hover:border-black transition-colors" data-testid={`s07-choice-${i}`}>
                {c.label}
              </button>
            ))}
          </div>
        )}
        {n.terminal && (
          <button type="button" onClick={reset} className="mt-5 text-sm font-medium underline underline-offset-4" data-testid="s07-restart">Run it again differently</button>
        )}
      </div>
      {path.length > 0 && (
        <ToolSection title="Your negotiation trail" testid="s07-trail">
          <div className="space-y-2">
            {path.map((p, i) => (
              <div key={i} className="border-l-2 border-zinc-300 pl-4 py-1">
                <div className="text-sm font-medium">{p.choice}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.cost} — {p.note}</div>
              </div>
            ))}
          </div>
        </ToolSection>
      )}
    </div>
  );
}

/* ---------- S08 Sales Call Coaching Scorecard ---------- */
const S08_BEHAVIOURS = [
  "Opened with a reason relevant to the buyer, not a company intro",
  "Asked about current situation before pitching",
  "Asked about impact ('what does that cost you?')",
  "Listened — buyer spoke at least half the time",
  "Evidenced claims with a specific proof or reference",
  "Handled the objection by exploring it, not countering it",
  "Proposed a specific next step with a date",
  "Confirmed the next step out loud before ending",
];
export function ToolS08() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const total = S08_BEHAVIOURS.reduce((s, _, i) => s + (scores[i] || 0), 0);
  const weakest = S08_BEHAVIOURS.map((b, i) => ({ b, s: scores[i] || 0 })).sort((a, b) => a.s - b.s)[0];
  return (
    <div data-testid="tool-S08" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">Score the call 0–2 per behaviour: 0 = didn't happen, 1 = partial, 2 = done well.</p>
        {S08_BEHAVIOURS.map((b, i) => (
          <div key={i} className="border border-zinc-200 p-3 flex items-center justify-between gap-4" data-testid={`s08-b-${i}`}>
            <span className="text-sm text-zinc-700 flex-1">{b}</span>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((v) => (
                <button key={v} type="button" onClick={() => setScores((s) => ({ ...s, [i]: v }))}
                  className={`w-8 h-8 text-xs border transition-colors ${scores[i] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`s08-${i}-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === S08_BEHAVIOURS.length ? (
          <div data-testid="s08-result">
            <ResultRow label="Call score" value={`${total} / 16`} strong tone={total >= 12 ? "good" : total >= 8 ? undefined : "bad"} />
            <ResultRow label="Coach first" value={weakest ? weakest.b : "—"} />
            <ToolSection title="The coaching rule">
              <p className="text-sm text-zinc-600 leading-relaxed">Coach one behaviour per week, not eight at once. Practice it in role-play on Tuesday, use it on calls by Thursday, review the recording on Monday.</p>
            </ToolSection>
          </div>
        ) : (
          <Note data-testid="s08-pending">Score all eight behaviours to get the coaching priority. {done}/8 done.</Note>
        )}
      </div>
    </div>
  );
}

/* ---------- S09 Proposal Win-Rate Diagnostic ---------- */
const S09_CHECKS = [
  { key: "theirwords", label: "Opens with the buyer's problem in the buyer's words" },
  { key: "quantified", label: "Quantifies the cost of the problem before any pricing" },
  { key: "outcomes", label: "Describes outcomes, not deliverables" },
  { key: "evidence", label: "Every claim carries proof (case, number, reference)" },
  { key: "options", label: "Presents priced options, not one take-it-or-leave-it" },
  { key: "nextstep", label: "Ends with a decision process, not 'looking forward'" },
  { key: "shortexec", label: "Executive summary fits one page and stands alone" },
];
export function ToolS09() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const weak = S09_CHECKS.filter((c, i) => (scores[i] || 0) <= 1);
  return (
    <div data-testid="tool-S09" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">Take your last lost proposal. Score each section 0–2 honestly.</p>
        {S09_CHECKS.map((c, i) => (
          <div key={c.key} className="border border-zinc-200 p-3 flex items-center justify-between gap-4" data-testid={`s09-${c.key}`}>
            <span className="text-sm text-zinc-700 flex-1">{c.label}</span>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((v) => (
                <button key={v} type="button" onClick={() => setScores((s) => ({ ...s, [i]: v }))}
                  className={`w-8 h-8 text-xs border transition-colors ${scores[i] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`s09-${i}-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === S09_CHECKS.length ? (
          <div data-testid="s09-result">
            <ResultRow label="Proposal strength" value={`${Object.values(scores).reduce((a, b) => a + b, 0)} / 14`} strong />
            <div className="mt-5 text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Rewrite priorities</div>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-700">
              {weak.map((c) => <li key={c.key}>{c.label}</li>)}
              {weak.length === 0 && <li>Strong on paper — if it still lost, the problem is upstream: qualification or the decision process.</li>}
            </ol>
            <Note>The most common fix: replace the company-overview chapter with a one-page restatement of the buyer's problem. Buyers forward proposals that make their internal case for them.</Note>
          </div>
        ) : <Note>Score all seven checks to see rewrite priorities. {done}/7 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- S10 Follow-Up Message Library (12 authored) ---------- */
const S10_LIBRARY = [
  { title: "After sending a proposal", timing: "Day 3", subject: "One question the proposal raises", body: "Hi [Name] — since Tuesday's proposal, one question has been nagging me: [specific risk in their situation]. Worth 15 minutes this week to pressure-test it? If the timing has slipped, a straight answer helps me plan too.", avoid: "'Just checking in' — it asks for attention and gives nothing." },
  { title: "After a good first call", timing: "Same day", subject: "What I heard — and one idea", body: "Hi [Name] — you said [their problem, their words] costs you roughly [their number]. Here's the one-page approach we discussed, plus one idea I didn't mention on the call: [specific]. Does Thursday work to walk your colleague through it?", avoid: "Recapping your services — recap their problem instead." },
  { title: "The deal gone quiet (2 weeks)", timing: "Day 14", subject: "Should I close the file?", body: "Hi [Name] — projects stall for good reasons: budget moved, priorities shifted, we missed something. If any of those is true, tell me and I'll plan accordingly. If it's still alive, what's the one thing that would move it?", avoid: "Guilt ('I've emailed three times') — it reads as pressure, not persistence." },
  { title: "After a demo", timing: "Next morning", subject: "The two things you reacted to", body: "Hi [Name] — you leaned forward twice yesterday: [feature A] and [outcome B]. I've put together what [outcome B] would look like in your first 90 days, with numbers. Who else should see it?", avoid: "A full feature list — anchor to what they reacted to." },
  { title: "Price objection", timing: "Within 24h", subject: "Options rather than a discount", body: "Hi [Name] — rather than shave price and value together, three options: [smaller scope / phased start / outcome-linked terms]. Each changes what you commit, not what you get. Which is closest to workable?", avoid: "Discounting in the reply — it reprices your credibility." },
  { title: "The internal champion", timing: "Before their internal meeting", subject: "For your Thursday meeting", body: "Hi [Name] — if this comes up Thursday, the three numbers your CFO will ask about are [x, y, z]. One-pager attached that says it better than a forwarded email chain. What would make it stronger?", avoid: "Making them assemble your business case for you." },
  { title: "Post-loss (graceful)", timing: "1 week after losing", subject: "Thank you — and one request", body: "Hi [Name] — genuinely, thanks for running a fair process. One request: a 10-minute call on what decided it. Not to change your mind — to fix what we can. And if the winner stumbles on [specific risk you saw], we're here.", avoid: "Re-litigating the decision." },
  { title: "The referral ask", timing: "After a visible win", subject: "Who else fights this problem?", body: "Hi [Name] — [specific result] is now live. Who in your network fights the same battle with [problem]? An introduction from you means more than a hundred cold emails from us.", avoid: "Asking before any result exists." },
  { title: "Quarterly value recap (existing client)", timing: "Quarterly", subject: "What changed this quarter", body: "Hi [Name] — this quarter: [result 1], [result 2], and the number you'll care about: [metric] moved from X to Y. Next quarter's plan attached. Twenty minutes to align?", avoid: "Activity logs — report outcomes, not effort." },
  { title: "Re-opening an old opportunity", timing: "6+ months later", subject: "Has anything changed with [problem]?", body: "Hi [Name] — when we spoke in [month], [problem] wasn't urgent enough. Two things changed since: [market change] and [new capability]. Worth a fresh look, or still parked?", avoid: "Pretending the first conversation never happened." },
  { title: "The slow no-decision", timing: "Week 6 of silence", subject: "What 'doing nothing' costs monthly", body: "Hi [Name] — staying as-is is a legitimate choice. It currently costs about [₹ figure you quantified together] per month. If that number is wrong, I'd rather know. If it's right, is it acceptable?", avoid: "Manufactured urgency — use the number you both agreed on." },
  { title: "New stakeholder joins", timing: "Within a week of the change", subject: "Context for your first month", body: "Hi [Name], congratulations on the move. We've been working with [colleague] on [problem] — here's the two-minute version of where things stand and what's decided. What would you like to see differently?", avoid: "Assuming the old deal terms survive the new stakeholder." },
];
export function ToolS10() {
  const [open, setOpen] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  return (
    <div data-testid="tool-S10">
      <p className="text-sm text-zinc-600 mb-5">Twelve messages for the moments deals go quiet — each written to send, with timing, a subject line and the language to avoid. Replace [brackets] with specifics; a template without specifics is spam.</p>
      <div className="space-y-2">
        {S10_LIBRARY.map((m, i) => (
          <div key={i} className="border border-zinc-200" data-testid={`s10-msg-${i}`}>
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 transition-colors" data-testid={`s10-toggle-${i}`}>
              <span className="text-sm font-medium">{i + 1}. {m.title}</span>
              <span className="text-xs text-zinc-500">{m.timing}</span>
            </button>
            {open === i && (
              <div className="border-t border-zinc-200 p-4 bg-zinc-50">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Subject</div>
                <p className="text-sm font-medium mt-1">{m.subject}</p>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-3">Message</div>
                <p className="text-sm text-zinc-700 mt-1 leading-relaxed whitespace-pre-line">{m.body}</p>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-3">Avoid</div>
                <p className="text-sm text-red-800 mt-1">{m.avoid}</p>
                <div className="mt-3">
                  <button type="button" data-testid={`s10-copy-${i}`}
                    onClick={async () => { setCopiedIdx((await copyText(`Subject: ${m.subject}\n\n${m.body}`)) ? i : -2); setTimeout(() => setCopiedIdx(null), 2500); }}
                    className="text-sm font-medium underline underline-offset-4">
                    {copiedIdx === i ? "Copied" : copiedIdx === -2 ? "Select & copy manually" : "Copy message"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- S11 Marketing–Sales SLA Builder ---------- */
const S11_PARTS = [
  ["leadDef", "Lead definition (what marketing commits to deliver)", "e.g. 'Fits ICP, named contact, stated need or behaviour, phone verified'"],
  ["routing", "Routing rule", "e.g. 'Assigned by territory within 2 business hours, named rep, no pool'"],
  ["response", "Response SLA (what sales commits)", "e.g. 'First contact attempt within 4 business hours, 6 attempts over 10 days'"],
  ["feedback", "Disposition & feedback", "e.g. 'Every lead dispositioned in CRM with reason code within 48h of last attempt'"],
  ["audit", "Monthly audit questions", "e.g. '% leads contacted in SLA · % dispositioned · top rejection reasons · conversion by source'"],
];
export function ToolS11() {
  const [v, setV] = useState({ leadDef: "", routing: "", response: "", feedback: "", audit: "" });
  const filled = Object.values(v).filter((x) => x.trim()).length;
  return (
    <div data-testid="tool-S11" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        {S11_PARTS.map(([k, label, hint]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`s11-${k}`} />
          </label>
        ))}
      </div>
      <div>
        <div className="border-2 border-black p-6 bg-white" data-testid="s11-preview">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Marketing–sales SLA · {filled}/5 sections</div>
          <div className="mt-4 space-y-4">
            {S11_PARTS.map(([k, label]) => (
              <div key={k}>
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">{label}</div>
                <p className="mt-1 text-sm leading-relaxed text-zinc-800">{v[k] || <span className="italic text-zinc-300">Undefined — the gap where leads die</span>}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4"><CopyButton testid="s11-copy" getText={() => `MARKETING–SALES SLA\n\n${S11_PARTS.map(([k, l]) => `${l.toUpperCase()}\n${v[k] || "-"}`).join("\n\n")}`} /></div>
        <Note tone={filled < 5 ? "warn" : "info"}>An SLA covers the two accusations that poison revenue teams: 'the leads were fine' and 'nobody followed up'. Without both halves in writing, the monthly meeting is a blame loop.</Note>
      </div>
    </div>
  );
}

/* ---------- S13 Commercial Review Dashboard ---------- */
const S13_KPIS = [
  ["Revenue vs plan", "₹ this month and YTD, vs plan and last year", "Sales head"],
  ["Contribution margin %", "After discounts, freight, credit cost — not gross margin", "Finance"],
  ["Pipeline coverage", "Qualified pipeline ÷ next quarter's target (healthy: 3×)", "Sales head"],
  ["Forecast accuracy", "Last month's forecast vs actual (healthy: ±10%)", "Sales head"],
  ["Collections / DSO", "Overdues by bucket, top 10 debtors named", "Finance"],
  ["Win/loss reasons", "Top 3 each, coded from interviews not CRM dropdowns", "Sales head"],
  ["Channel health", "Secondary vs primary, partner inventory weeks", "Channel head"],
  ["Pricing realisation", "Pocket price ÷ list, trend line", "Commercial head"],
];
export function ToolS13() {
  const [vals, setVals] = useState({});
  const [decisions, setDecisions] = useState("");
  const filled = Object.values(vals).filter((v) => String(v).trim()).length;
  return (
    <div data-testid="tool-S13">
      <p className="text-sm text-zinc-600 mb-5">One page, eight KPIs, one owner each. Fill actuals, run the meeting, log the decisions. The discipline is the product.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {S13_KPIS.map(([name, def, owner], i) => (
          <div key={name} className="border border-zinc-200 p-4" data-testid={`s13-kpi-${i}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{name}</span>
              <span className="text-xs text-zinc-400">{owner}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">{def}</p>
            <input value={vals[i] || ""} onChange={(e) => setVals((s) => ({ ...s, [i]: e.target.value }))} placeholder="This month's actual…" className="mt-2 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`s13-val-${i}`} />
          </div>
        ))}
      </div>
      <ToolSection title="Decision log — what did we decide, who owns it, by when" testid="s13-log">
        <textarea value={decisions} onChange={(e) => setDecisions(e.target.value)} rows={3} placeholder="e.g. 1) Cut SKUs below 2 weeks cover — Ravi, 15th. 2) Escalate top-3 overdues to CFO — Meera, Friday." className="w-full border border-zinc-300 p-3 text-sm outline-none focus:border-black" data-testid="s13-decisions" />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-zinc-500">{filled}/8 KPIs filled</span>
          <CopyButton testid="s13-copy" getText={() => `MONTHLY COMMERCIAL REVIEW\n\n${S13_KPIS.map(([n, , o], i) => `${n} (${o}): ${vals[i] || "—"}`).join("\n")}\n\nDECISIONS\n${decisions || "—"}`} />
        </div>
      </ToolSection>
      <Note tone="warn">A review that ends without a decision log is a status update. Every month should produce at least one decision someone would notice if it didn't happen.</Note>
    </div>
  );
}

/* ---------- S14 Commercial Operating Model Canvas ---------- */
const S14_BLOCKS = [
  ["decisions", "Key commercial decisions", "Price changes, deal approvals, channel adds, hires — list the ten that matter"],
  ["rights", "Decision rights (RAPID-style)", "Who Recommends, Agrees, Performs, Inputs, Decides — per decision"],
  ["rhythm", "Operating rhythm", "Weekly pipeline, monthly commercial review, quarterly JBPs — with durations"],
  ["metrics", "Metrics each role owns", "One primary number per role — the one they're fired for"],
  ["handoffs", "Handoffs that leak", "Marketing→sales, sales→delivery, sales→finance — name the drop points"],
];
export function ToolS14() {
  const [v, setV] = useState({ decisions: "", rights: "", rhythm: "", metrics: "", handoffs: "" });
  const filled = Object.values(v).filter((x) => x.trim()).length;
  return (
    <div data-testid="tool-S14">
      <p className="text-sm text-zinc-600 mb-5">Who owns which commercial decision? Draw it on one canvas — five blocks, brutal clarity. Empty blocks are your operating-model gaps.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {S14_BLOCKS.map(([k, label, hint]) => (
          <div key={k} className={`border p-4 transition-colors ${v[k].trim() ? "border-zinc-200" : "border-dashed border-red-300"}`} data-testid={`s14-${k}`}>
            <div className="text-sm font-medium">{label}</div>
            <p className="text-xs text-zinc-500 mt-1">{hint}</p>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={4} className="mt-2 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`s14-input-${k}`} />
          </div>
        ))}
        <div className="border border-zinc-200 p-4 bg-zinc-50 flex flex-col justify-between" data-testid="s14-result">
          <div>
            <div className="text-sm font-medium">Canvas completeness</div>
            <div className="mt-2 font-serif-display text-4xl">{filled}/5</div>
            <p className="text-xs text-zinc-500 mt-2">{filled < 5 ? "Every empty block is a decision currently made by accident, habit or the loudest voice." : "Complete canvas. Now test it: pick last month's three hardest decisions and trace them through it."}</p>
          </div>
          <div className="mt-4"><CopyButton testid="s14-copy" getText={() => `COMMERCIAL OPERATING MODEL\n\n${S14_BLOCKS.map(([k, l]) => `${l.toUpperCase()}\n${v[k] || "— GAP —"}`).join("\n\n")}`} /></div>
        </div>
      </div>
    </div>
  );
}
